import type {
  ImportTemplate,
  Page,
  ProjectTemplate,
  ProjectTemplateSummary,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/project-templates');

/** `GET /admin/v1/project-templates` (summaries; seeded + saved). */
export function useTemplates(pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<ProjectTemplateSummary>({
    queryKey: keys.templates.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<ProjectTemplateSummary>>('GET', base, {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
  });
}

/** `GET /admin/v1/project-templates/{tid}` (with the JSON `document`). */
export function useTemplate(templateId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.templates.detail(templateId ?? ''),
    queryFn: ({ signal }) => request<ProjectTemplate>('GET', `${base}/${templateId}`, { signal }),
    enabled: templateId !== undefined,
  });
}

/** `POST /admin/v1/project-templates/import` (JSON document upload). */
export function useImportTemplate() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: ImportTemplate) =>
      request<ProjectTemplate>('POST', `${base}/import`, { body: input }),
    onSuccess: () => invalidate(keys.templates.all),
  });
}

export function useDeleteTemplate() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${base}/${id}`),
    onSuccess: (_r, id) => {
      remove(keys.templates.detail(id));
      return invalidate(keys.templates.all);
    },
  });
}
